import React, { type ReactNode } from 'react';
import {
  type GetInputPropsReturnValue,
  type GetItemPropsReturnValue,
  type GetLabelPropsReturnValue,
  type GetMenuPropsReturnValue,
  type UseComboboxGetToggleButtonPropsReturnValue,
  type UseSelectGetToggleButtonReturnValue,
} from 'downshift';

import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import ChevronDownIcon from '../icons/ChevronDownIcon';
import ChevronUpIcon from '../icons/ChevronUpIcon';
import TickIcon from '../icons/TickIcon';

import { Box } from '../box/box';
import { Card } from '../card/card';
import { CheckboxControl } from '../checkbox/checkbox-control';
import { GrandEntrance } from '../grand-entrance/grand-entrance';
import { Text } from '../text/text';
import { textVariantToStyles } from '../text/text-styles';

import { getSharedProps, sizeConfig } from './utilities';

import * as styles from './dropdown.css';

export type DropdownSize = 'medium' | 'large';

export interface DropdownItem<Value> {
  /* NOTE: the label cannot use <FormattedMessage /> because the onlyText
   * function cannot extract the text.
   **/
  label: string;
  value: Value;
  disabled?: boolean;
}

export type DropdownItems<Value> = Array<DropdownItem<Value>>;

export interface GetSharedProps {
  size: DropdownSize;
  disabled: boolean;
  hasError?: boolean;
}

export type ControlledProps<Value> = {
  /* Supply a value to make component controlled
   * If you want a placeholder value use an empty string ('')
   **/
  onChange: (value: Value) => void;
  value?: Value | '';
  defaultValue?: never;
};

type UncontrolledProps<Value> = {
  onChange?: (value: Value) => void;
  value?: never;
  defaultValue?: Value | undefined;
};

type ControlledOrUncontrolled<Value = string> =
  | ControlledProps<Value>
  | UncontrolledProps<Value>;

export type UseDropdownOptions<Value = string> = {
  /* NOTE: the label on each item cannot use <FormattedMessage />
   * because the onlyText function cannot extract the text.
   **/
  items: DropdownItems<Value>;
  /*
   *  when using with conform we need to supply the id instead of using an internally generated one
   **/
  id?: string;
} & ControlledOrUncontrolled<Value>;

interface DropdownProps {
  expand: boolean;
  label: ReactNode;
  disabled: boolean;
  labelProps: GetLabelPropsReturnValue;
  children: ReactNode;
}

export const Dropdown = ({
  label,
  expand,
  disabled,
  labelProps,
  children,
}: DropdownProps) => {
  return (
    <Box spacing="baseNeg4" expand={expand}>
      <Text
        {...labelProps}
        variant="caption"
        truncate
        color={disabled ? 'neutral250' : undefined}
      >
        {label}
      </Text>

      {children}
    </Box>
  );
};

interface MenuItemProps extends Partial<GetInputPropsReturnValue> {
  highlightedIndex: number;
  item: DropdownItem<unknown>;
  index: number;
  size: DropdownSize;
  isSelected: boolean;
}

const MenuItem = ({
  highlightedIndex,
  index,
  item,
  size,
  isSelected,
  ...itemProps
}: MenuItemProps) => {
  const { itemPadding } = getSharedProps({
    size,
    disabled: false,
  });

  return (
    <Box
      Component="li"
      stack="row"
      alignContent={{ y: 'center' }}
      key={String(item.value)}
      {...(item.disabled ? {} : itemProps)}
      className={classFilter([
        highlightedIndex === index && styles.highlightedItem,
      ])}
    >
      {/**
       *  We only apply vertical padding to the text, if we added it to the
       *  Icon if would make the item too tall
       */}
      <Text
        variant="input"
        padding={itemPadding.text}
        color={item.disabled ? 'neutral250' : 'currentColor'}
      >
        {item.label}
      </Text>

      <Box padding={itemPadding.chevron}>
        {isSelected ? <TickIcon /> : null}
      </Box>
    </Box>
  );
};

interface MenuItemWithCheckboxProps<Value = string>
  extends Partial<GetInputPropsReturnValue> {
  name: string;
  highlightedIndex: number;
  item: DropdownItem<Value>;
  index: number;
  size: DropdownSize;
  defaultChecked?: boolean;
  id: string;
  value?: string;
  disabled?: boolean;
  checkBoxProps: Partial<Omit<GetItemPropsReturnValue, 'role'>>;
}

const MenuItemWithCheckbox = ({
  highlightedIndex,
  index,
  item,
  size,
  defaultChecked,
  name,
  value,
  id,
  disabled,
  checkBoxProps,
  ...itemProps
}: MenuItemWithCheckboxProps) => {
  const { itemPadding } = getSharedProps({
    size,
    disabled: false,
  });

  return (
    <Box
      Component="li"
      stack="row"
      alignContent={{ y: 'center' }}
      key={String(item.value)}
      {...(item.disabled ? {} : itemProps)}
      className={classFilter([
        highlightedIndex === index && styles.highlightedItem,
      ])}
    >
      <Box padding={itemPadding.checkBox}>
        <CheckboxControl
          id={id}
          label={item.label}
          name={name}
          defaultChecked={defaultChecked}
          screenReaderOnlyLabel={true}
          value={value}
          disabled={disabled}
          {...checkBoxProps}
        />
      </Box>

      {/**
       *  We only apply vertical padding to the text, if we added it to the
       *  Icon if would make the item too tall
       */}
      <Text
        variant="input"
        padding={itemPadding.text}
        color={item.disabled ? 'neutral250' : 'currentColor'}
      >
        {item.label}
      </Text>
    </Box>
  );
};

const NoResults = ({ children }: { children: ReactNode }) => {
  return (
    <Box padding="base" color="neutral250" Component="li">
      <Text variant="input">{children}</Text>
    </Box>
  );
};

type DropdownButtonProps = {
  disabled: boolean;
  readOnly?: boolean;
  size: DropdownSize;
  isOpen: boolean;
  toggleButtonProps: Omit<
    | UseSelectGetToggleButtonReturnValue
    | UseComboboxGetToggleButtonPropsReturnValue,
    'ref'
  >;
  children: ReactNode;
  /**
   * Override of logic to show outline error styling on dropdown without validation messages
   */
  showVisualErrorStyles?: boolean;
} & (
  | {
      toggleButtonVariant: 'container';
      ref?: never;
    }
  | {
      toggleButtonVariant: 'toggle';
      ref: React.Ref<HTMLElement>;
    }
);

/**
 * For the `combobox` (search) variant only the chevron carries the toggle
 * props, and it renders as a plain `div` that is deliberately outside the tab
 * order — the search input itself is the control, and downshift already gives
 * it `role="combobox"` along with the `aria-expanded` / `aria-controls` state.
 * A `div` has no role, and `aria-expanded` is not a global ARIA attribute, so
 * leaving it here fails axe's `aria-allowed-attr` rule. Drop the popup ARIA and
 * keep the chevron as the mouse affordance it is.
 */
const withoutPopupAria = ({
  'aria-expanded': _ariaExpanded,
  'aria-controls': _ariaControls,
  ...toggleButtonProps
}: DropdownButtonProps['toggleButtonProps']) => toggleButtonProps;

const DropdownButton = ({
  children,
  size,
  disabled = false,
  readOnly = false,
  isOpen,
  toggleButtonProps,
  ref,
  toggleButtonVariant = 'container',
  showVisualErrorStyles = false,
}: DropdownButtonProps) => {
  const { itemPadding, stateStyles, boxProps } = getSharedProps({
    size,
    disabled: disabled || readOnly,
    hasError: showVisualErrorStyles,
  });

  return (
    <Box
      className={classFilter([stateStyles, styles.button])}
      variant="grid"
      expand
      {...boxProps}
      disabled={disabled}
      padding="none"
      ref={ref}
      // we want the whole button to be a toggle for the `select` variant
      {...(toggleButtonVariant === 'container' ? toggleButtonProps : {})}
    >
      <Box
        gridItem={{ rowStart: '1', columnEnd: '2', columnStart: '1' }}
        variant={toggleButtonVariant === 'container' ? 'flex' : 'grid'}
        expand
      >
        {children}
      </Box>

      <Box
        padding={itemPadding.chevron}
        alignContent={{ y: 'center' }}
        color={boxProps.color}
        gridItem={{ rowStart: '1', columnStart: '2' }}
        // we only want the chevron to be a toggle for the `combobox` (search) variant
        {...(toggleButtonVariant === 'toggle' && !readOnly && !disabled
          ? withoutPopupAria(toggleButtonProps)
          : {})}
      >
        {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </Box>
    </Box>
  );
};

type SearchInputProps<Value> = Omit<
  DropdownButtonProps,
  'children' | 'toggleButtonVariant'
> & {
  getInputProps: () => GetInputPropsReturnValue;
  selectedItem: DropdownItem<Value> | null;
  placeholder?: string;
  ref: React.Ref<HTMLElement>;
  name: string;
  readOnly?: boolean;
  showVisualErrorStyles?: boolean;
};

export const SearchInput = <Value,>({
  size,
  disabled = false,
  isOpen,
  toggleButtonProps,
  ref,
  getInputProps,
  selectedItem,
  placeholder,
  name,
  readOnly = false,
  showVisualErrorStyles,
  ...props
}: SearchInputProps<Value>) => {
  const readOnlyOrDisabled = disabled || readOnly;

  const { inputResetStyles } = getSharedProps({
    size,
    disabled: readOnlyOrDisabled,
    hasError: showVisualErrorStyles,
  });

  const inputProps = getInputProps();

  const value =
    selectedItem?.value !== undefined && selectedItem?.value !== null
      ? String(selectedItem.value)
      : '';

  return (
    <Dropdown.Button
      size={size}
      disabled={disabled}
      readOnly={readOnly}
      isOpen={isOpen}
      toggleButtonProps={toggleButtonProps}
      toggleButtonVariant="toggle"
      ref={ref}
    >
      <input type="hidden" name={name} value={value || ''} />

      <Box
        {...(!readOnlyOrDisabled
          ? inputProps
          : // manually prevent the input events in readOnly mode
            {
              ...inputProps,
              onFocus: undefined,
              onBlur: undefined,
              onClick: undefined,
              onKeyDown: undefined,
            })}
        variant="flex"
        Component="input"
        color="currentColor"
        gridItem={{ rowStart: '1', columnEnd: '3', columnStart: '1' }}
        rounded
        disabled={disabled}
        readOnly={readOnly}
        className={classFilter([
          inputResetStyles,
          styles.search,
          textVariantToStyles('input'),
        ])}
        padding={{
          y: sizeConfig[size].paddingY,
          left: 'base',
          right: 'base',
        }}
        placeholder={selectedItem?.label || placeholder}
        {...props}
      />
    </Dropdown.Button>
  );
};

type MultipleSearchInputProps = Omit<
  DropdownButtonProps,
  'children' | 'toggleButtonVariant'
> & {
  getInputProps: () => GetInputPropsReturnValue;
  placeholder?: string;
  ref: React.Ref<HTMLElement>;
  showVisualErrorStyles?: boolean;
};

export const MultipleSearchInput = ({
  size,
  disabled = false,
  isOpen,
  toggleButtonProps,
  ref,
  getInputProps,
  placeholder,
  showVisualErrorStyles,
  ...props
}: MultipleSearchInputProps) => {
  const readOnlyOrDisabled = disabled;

  const { inputResetStyles } = getSharedProps({
    size,
    disabled: readOnlyOrDisabled,
    hasError: showVisualErrorStyles,
  });

  const inputProps = getInputProps();

  return (
    <Dropdown.Button
      size={size}
      disabled={disabled}
      isOpen={isOpen}
      toggleButtonProps={toggleButtonProps}
      toggleButtonVariant="toggle"
      ref={ref}
      showVisualErrorStyles={showVisualErrorStyles}
    >
      <Box
        {...(!readOnlyOrDisabled
          ? inputProps
          : // manually prevent the input events in readOnly mode
            {
              ...inputProps,
              onFocus: undefined,
              onBlur: undefined,
              onClick: undefined,
              onKeyDown: undefined,
            })}
        variant="flex"
        Component="input"
        color="currentColor"
        gridItem={{ rowStart: '1', columnEnd: '3', columnStart: '1' }}
        rounded
        disabled={disabled}
        className={classFilter([
          inputResetStyles,
          styles.search,
          textVariantToStyles('input'),
        ])}
        padding={{
          y: sizeConfig[size].paddingY,
          left: 'base',
          right: 'base',
        }}
        placeholder={placeholder}
        {...props}
      />
    </Dropdown.Button>
  );
};

export interface MenuContainerProps {
  floatingStyles: React.CSSProperties;
  constrainStyles: React.CSSProperties | undefined;
  setFloating: (node: HTMLElement | null) => void;
  disabled: boolean;
  isOpen: boolean;
  children: ReactNode;
  menuProps: GetMenuPropsReturnValue;
}

const MenuContainer = ({
  floatingStyles,
  setFloating,
  disabled,
  isOpen,
  constrainStyles,
  children,
  menuProps,
}: MenuContainerProps) => {
  return (
    <Box
      padding={{ top: 'baseNeg6' }}
      className={styles.floating}
      style={floatingStyles}
      ref={setFloating}
    >
      <GrandEntrance
        show={!disabled && isOpen}
        animateHeight={true}
        config={
          isOpen
            ? {
                mass: 1,
                tension: 260,
                friction: 10,
                precision: 0.01,
                clamp: true,
                velocity: 0.002, // seconds
              }
            : {
                duration: 0,
              }
        }
      >
        <Card
          Component="ul"
          padding="none"
          {...menuProps}
          style={
            constrainStyles // sets the dimensions
          }
          constrain="scroll"
        >
          {children}
        </Card>
      </GrandEntrance>
    </Box>
  );
};

Dropdown.Button = DropdownButton;
Dropdown.MenuItem = MenuItem;
Dropdown.MenuItemWithCheckbox = MenuItemWithCheckbox;
Dropdown.NoResults = NoResults;
Dropdown.Menu = MenuContainer;
Dropdown.SearchInput = SearchInput;
Dropdown.MultipleSearchInput = MultipleSearchInput;
